import { useRequest } from "@certn/api";
import { Text, Link } from "@certn/cascadia";
import { BasicButton, Card, SectionMessage, Icon } from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import {
  downloadAllDocuments,
  type DocumentUrlsResponse,
} from "../utils/legacyCaseDownloads";

interface Props {
  reportId: string | undefined;
}

interface LegacyCaseDetails {
  applicant_first_name: string;
  applicant_last_name: string;
}

export function LegacyDocumentsTab({ reportId }: Props) {
  const { t } = useTranslation();
  const request = useRequest();

  const [documentUrls, setDocumentUrls] = useState<DocumentUrlsResponse | null>(
    null
  );
  const [legacyCaseDetails, setLegacyCaseDetails] =
    useState<LegacyCaseDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<any>(null);

  useEffect(() => {
    if (!reportId) return;

    const fetchDocuments = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Fetch document URLs
        const documentsResponse = (await request(
          `/api/internal/client-portal/legacy-case/${reportId}/documents/`
        )) as DocumentUrlsResponse;
        setDocumentUrls(documentsResponse);

        // Fetch case details for applicant name
        const caseDetailsResponse = (await request(
          `/api/internal/client-portal/legacy-case/${reportId}/`
        )) as LegacyCaseDetails;
        setLegacyCaseDetails(caseDetailsResponse);
      } catch (error) {
        console.error("Failed to fetch documents:", error);
        setError(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]);

  const getApplicantFullName = () => {
    if (!legacyCaseDetails) return "";
    return `${legacyCaseDetails.applicant_first_name} ${legacyCaseDetails.applicant_last_name}`;
  };

  const getFileName = (url: string, defaultName: string) => {
    try {
      // Extract filename from URL or use default
      const urlPath = new URL(url).pathname;
      const fileName = urlPath.split("/").pop();
      return fileName && fileName !== "" ? fileName : defaultName;
    } catch {
      return defaultName;
    }
  };

  const handleDownloadAll = async () => {
    if (!documentUrls || !reportId) return;
    await downloadAllDocuments(documentUrls, reportId);
  };

  if (error) {
    return (
      <div data-testid="legacy-documents">
        <Card
          className={styles.cardContainer}
          type="large"
          responsive
          testID="legacy-documents-card"
          border="border-base"
        >
          <Text style="global/heading-large">{t("Documents")}</Text>
          <SectionMessage
            type="danger"
            icon="error"
            sectionTitle={t("Error loading documents")}
          >
            {t(
              "Unable to load documents for this case, please try again later."
            )}
          </SectionMessage>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div data-testid="legacy-documents">
        <Card
          className={styles.cardContainer}
          type="large"
          responsive
          testID="legacy-documents-loading-card"
          border="border-base"
        >
          <Text style="global/heading-large">{t("Documents")}</Text>
          <Text>{t("Loading documents...")}</Text>
        </Card>
      </div>
    );
  }

  const documents = [];

  // Add PDF report if available
  if (documentUrls?.pdf_report) {
    documents.push({
      url: documentUrls.pdf_report,
      fileName: getFileName(documentUrls.pdf_report, "Report.pdf"),
      type: "PDF Report",
    });
  }

  // Add CSV report if available
  if (documentUrls?.csv_report) {
    documents.push({
      url: documentUrls.csv_report,
      fileName: getFileName(documentUrls.csv_report, "Report.csv"),
      type: "CSV Report",
    });
  }

  // Add consent documents if available
  if (
    documentUrls?.consent_documents &&
    documentUrls.consent_documents.length > 0
  ) {
    documentUrls.consent_documents.forEach((url, index) => {
      documents.push({
        url,
        fileName: getFileName(url, `Consent Document ${index + 1}.pdf`),
        type: "Consent Document",
      });
    });
  }

  return (
    <div data-testid="legacy-documents">
      <Card
        className={styles.cardContainer}
        type="large"
        responsive
        testID="legacy-documents-card"
        border="border-base"
      >
        <div className={styles.header}>
          <Text style="global/heading-large">{t("Documents")}</Text>
          {documents.length > 0 && (
            <BasicButton
              leftIcon="download"
              onPress={handleDownloadAll}
              testID="download-all-documents-button"
            >
              {t("Download all documents")}
            </BasicButton>
          )}
        </div>

        {documents.length === 0 ? (
          <div className={styles.emptyState}>
            <Text style="global/body-secondary">
              {t("No documents are available for this case.")}
            </Text>
          </div>
        ) : (
          <div className={styles.documentsContainer}>
            {documents.map((document, index) => (
              <div key={index} className={styles.documentRow}>
                <div className={styles.iconAndText}>
                  <Icon name="image" />
                  <div className={styles.documentInfo}>
                    <Text style="global/heading-small">
                      {document.fileName}
                    </Text>
                    <Text style="global/body-secondary">
                      {getApplicantFullName()}
                    </Text>
                  </div>
                </div>
                <Link
                  href={document.url}
                  target="_blank"
                  linkStyle="monochrome"
                  hasUnderline={true}
                  testID={`document-view-link-${index}`}
                >
                  {t("View")}
                </Link>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

const styles = {
  cardContainer: css({
    padding: "var(--space-5) var(--space-4)",
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-4)",
  }),
  header: css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "var(--space-4)",
    flexWrap: "wrap",
  }),
  documentsContainer: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-3)",
  }),
  documentRow: css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "var(--space-3)",
    borderBottom: "1px solid var(--border-base)",
    "&:last-child": {
      borderBottom: "none",
    },
  }),
  iconAndText: css({
    display: "flex",
    alignItems: "center",
    gap: "var(--space-2)",
  }),
  documentInfo: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-1)",
  }),
  emptyState: css({
    display: "flex",
    justifyContent: "center",
    padding: "var(--space-6)",
    backgroundColor: "var(--surface-sunken)",
    borderRadius: "var(--border-radius-1)",
    textAlign: "center",
  }),
};
